import { useState, useMemo } from 'react';

import { createContexts } from '@/js/libs/create-contexts/index.ts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { useCallbackRef$ } from '@/js/libs/observable/index.ts';

import type { RegisteredSection, IntersectingItem, ScrollToOptions } from './types.ts';
import { useDirection } from './useDirection.ts';
import { useIntersectedItems } from './useIntersectedItems.ts';
import { useScrollTo } from './useScrollTo.ts';

interface RegisterSectionParams {
  id: string;
  title: string;
  ref: { current: HTMLElement | null };
  order: number;
  error?: boolean;
  dataTestId?: string;
}

export const { useScrollNavigationContext } = createContexts({
  ScrollNavigation(offset: number) {
    /**
     * Sections can be updated in different order after initial render. So it stored
     */
    const [sections, setSections] = useState<Map<string, RegisteredSection>>(new Map());
    const [activeId, setActiveId] = useState<string | undefined>();

    const { isScrollingRef, scrollTo } = useScrollTo(offset);
    const direction$ = useDirection(isScrollingRef);

    const registerSection = useCallbackRef((params: RegisterSectionParams) => {
      setSections((prev) => {
        const next = new Map(prev);
        next.set(params.id, params);
        return next;
      });
    });

    const unregisterSection = useCallbackRef((id: string) => {
      setSections((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    });

    const scrollToSection = useCallbackRef((id: string, options?: ScrollToOptions) => {
      const section = sections.get(id);
      if (!section) {
        // eslint-disable-next-line no-console
        console.warn(`Section with id "${id}" not found`);
        return;
      }
      const element = section.ref.current;
      if (!element) {
        // eslint-disable-next-line no-console
        console.warn(`Element binded to section"${id}" is unmounted`);
        return;
      }
      setActiveId(id);
      scrollTo(element, options);
    });

    const intersectionObserverSettings = useMemo(
      () => ({
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
        rootMargin: `-${offset}px 0px 0px`,
      }),
      [offset],
    );

    const onIntersected = useCallbackRef$((ctx) => (intersectingItems: IntersectingItem[]) => {
      if (isScrollingRef.current) return;

      const allSections = Array.from(sections.values());

      if (intersectingItems.length === 0) {
        setActiveId(allSections[0]?.id);
        return;
      }

      const direction = ctx.get(direction$);
      const sortedItems = [...intersectingItems].sort((a, b) => {
        if (b.intersectionRatio !== a.intersectionRatio) {
          return b.intersectionRatio - a.intersectionRatio;
        }

        const aTop = a.target.getBoundingClientRect().top;
        const bTop = b.target.getBoundingClientRect().top;

        if (direction === null) return aTop - bTop;
        return direction === 'down' ? bTop - aTop : aTop - bTop;
      });

      const topItem = sortedItems.at(0);
      if (topItem) {
        setActiveId(topItem.id);
      }
    });

    useIntersectedItems(onIntersected, intersectionObserverSettings, sections);

    const sectionsList = useMemo(() => Array.from(sections.values()).sort((a, b) => a.order - b.order), [sections]);

    return {
      offset,
      registerSection,
      unregisterSection,
      scrollToSection,
      activeId,
      sections: sectionsList,
    };
  },
});
